import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDate, stripHtml } from '../utils';
import { nominatimSearch } from '../nominatim';
import Modal from './Modal';

const toYtEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u    = new URL(url.trim());
    const host = u.hostname.replace('www.', '').replace('m.', '');
    let id = null;
    if (host === 'youtu.be') { id = u.pathname.slice(1).split('/')[0]; }
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/')) { id = u.pathname.split('/')[2]; }
      else { id = u.searchParams.get('v'); }
    }
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) return `https://www.youtube-nocookie.com/embed/${id}`;
  } catch {}
  return null;
};

// ─── MAP TIMELINE ──────────────────────────────────────────────────────────────
//
//  Tier 1: Nominatim compliance, offline fallback, coord validation, --vh, toast
//  Tier 2: Route lines, category pins, heatmap, map search, event filter
//  Tier 3: Satellite tiles, measurement tool, weather (Open-Meteo), PNG/PDF export
//
//  Props: events, colors, onEventClick, manualOrder, mapCenter
//

const SPEEDS = [
  { label: 'Slow',   ms: 6000 },
  { label: 'Normal', ms: 3000 },
  { label: 'Fast',   ms: 1500 },
];

const FALLBACK_TILE_URL =
  import.meta.env?.VITE_FALLBACK_TILE_URL ||
  'https://tile.openstreetmap.de/{z}/{x}/{y}.png';

const TILES = {
  standard: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attr: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    sub: 'abc',
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attr: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    sub: 'abc',
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attr: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    sub: 'abcd',
  },
  satellite: {
    // ESRI World Imagery — free, no API key, CORS-enabled
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attr: 'Tiles &copy; Esri &mdash; Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP and the GIS User Community',
    sub: '',
  },
  fallback: {
    url: FALLBACK_TILE_URL,
    attr: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    sub: '',
  },
};

// ─── WMO weather code lookup ───────────────────────────────────────────────────

const WMO = {
  0:  ['Clear sky',          '☀️'],  1:  ['Mainly clear',       '🌤️'],
  2:  ['Partly cloudy',      '⛅'],  3:  ['Overcast',           '☁️'],
  45: ['Fog',                '🌫️'],  48: ['Rime fog',           '🌫️'],
  51: ['Light drizzle',      '🌦️'],  53: ['Drizzle',            '🌦️'],  55: ['Dense drizzle',      '🌦️'],
  61: ['Light rain',         '🌧️'],  63: ['Rain',               '🌧️'],  65: ['Heavy rain',         '🌧️'],
  71: ['Light snow',         '❄️'],  73: ['Snow',               '❄️'],  75: ['Heavy snow',         '❄️'],
  77: ['Snow grains',        '🌨️'],
  80: ['Rain showers',       '🌦️'],  81: ['Rain showers',       '🌦️'],  82: ['Violent showers',    '⛈️'],
  85: ['Snow showers',       '🌨️'],  86: ['Heavy snow showers', '🌨️'],
  95: ['Thunderstorm',       '⛈️'],  96: ['Thunderstorm',       '⛈️'],  99: ['Thunderstorm',       '⛈️'],
};

// ─── Category color palette ────────────────────────────────────────────────────

const CAT_PALETTE = [
  '#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6',
  '#8b5cf6','#ec4899','#84cc16','#14b8a6','#a855f7','#78716c',
];
const _catMap = {};
let _catIdx = 0;

function categoryColor(ev, fallback) {
  if (!ev?.category) return fallback;
  const key = ev.category.toLowerCase();
  if (!_catMap[key]) _catMap[key] = CAT_PALETTE[_catIdx++ % CAT_PALETTE.length];
  return _catMap[key];
}

// ─── Coordinate validation ─────────────────────────────────────────────────────

function isValidLat(v) { return typeof v === 'number' && isFinite(v) && v >= -90  && v <= 90;  }
function isValidLng(v) { return typeof v === 'number' && isFinite(v) && v >= -180 && v <= 180; }
function hasValidCoords(ev) {
  return isValidLat(ev?.location?.lat) && isValidLng(ev?.location?.lng);
}

// ─── Misc helpers ──────────────────────────────────────────────────────────────

function detectDarkTheme() {
  const t = document.documentElement.dataset.theme;
  if (t) return t === 'dark';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function kmBetween(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatKm(km) {
  return km >= 1000
    ? `${(km / 1000).toFixed(1)} Mm`
    : km >= 1
    ? `${Math.round(km)} km`
    : `${Math.round(km * 1000)} m`;
}

function routeDistance(events) {
  const pts = events.filter(hasValidCoords);
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    total += kmBetween(
      pts[i - 1].location.lat, pts[i - 1].location.lng,
      pts[i].location.lat,     pts[i].location.lng
    );
  }
  return total;
}

function makePinIcon(isActive, primary, secondary, pinColor = null) {
  const color = isActive ? secondary : (pinColor || primary);
  const size  = isActive ? 22 : 14;
  const pulse = isActive
    ? `<div class="mp-pin-pulse" style="border-color:${secondary};"></div>`
    : '';
  return L.divIcon({
    className: 'mp-pin-host',
    html: `<div class="mp-pin${isActive ? ' mp-pin-active' : ''}" style="width:${size}px;height:${size}px;background:${color};box-shadow:0 0 0 2px #fff, 0 2px 8px rgba(0,0,0,.4);">${pulse}</div>`,
    iconSize:    [size + 8, size + 8],
    iconAnchor:  [(size + 8) / 2, size + 8],
    popupAnchor: [0, -(size + 8)],
  });
}

function makeClusterIcon(count, primary) {
  return L.divIcon({
    className: 'mp-cluster-host',
    html: `<div class="mp-cluster" style="background:${primary};">${count}</div>`,
    iconSize:   [36, 36],
    iconAnchor: [18, 18],
  });
}

// Uses html2canvas with useCORS so cross-origin OSM/ESRI tiles render correctly.
// UI overlays are excluded via ignoreElements so only the map canvas is captured.
async function getMapCanvas(mapEl) {
  return html2canvas(mapEl, {
    useCORS:      true,
    allowTaint:   false,
    logging:      false,
    scale:        window.devicePixelRatio || 1,
    // Ignore UI controls overlaid on the map — only capture the tile/marker layer
    ignoreElements: (el) => el.classList?.contains('mp-map-controls') ||
                            el.classList?.contains('mp-tile-toggle')   ||
                            el.classList?.contains('mp-map-search')    ||
                            el.classList?.contains('mp-preview')       ||
                            el.classList?.contains('mp-preview-reopen')||
                            el.classList?.contains('mp-offline-banner')||
                            el.classList?.contains('mp-measure-banner')||
                            el.classList?.contains('mp-toast'),
  });
}

// ─────────────────────────────────────────────────────────────────────────────

const MapTimeline = ({ events = [], colors = {}, onEventClick, manualOrder = false, mapCenter = null, timeline = null, isEmbed = false }) => {
  const primary   = colors.primary   || '#FFB612';
  const secondary = colors.secondary || '#A8A9AD';

  const sorted = useMemo(() => {
    return [...events].sort((a, b) => {
      if (manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      const d = (a.startDate ?? '').localeCompare(b.startDate ?? '');
      return d !== 0 ? d : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
  }, [events, manualOrder]);

  // ── Core state ─────────────────────────────────────────────────────────────
  const [activeIdx,     setActiveIdx]     = useState(0);
  const [tileStyle,     setTileStyle]     = useState(() => detectDarkTheme() ? 'dark' : 'standard');
  const [playing,       setPlaying]       = useState(false);
  const [progress,      setProgress]      = useState(0);
  const [playSpeed,     setPlaySpeed]     = useState(1);
  const [showPreview,   setShowPreview]   = useState(false);
  const [mapReady,      setMapReady]      = useState(false);

  // ── Tier 1 state ───────────────────────────────────────────────────────────
  const [offlineBanner, setOfflineBanner] = useState(false);
  const [toast,         setToast]         = useState('');
  const [toastVisible,  setToastVisible]  = useState(false);

  // ── Tier 2 state ───────────────────────────────────────────────────────────
  const [showRoute,     setShowRoute]     = useState(false);
  const [showHeatmap,   setShowHeatmap]   = useState(false);
  const [mapSearch,     setMapSearch]     = useState('');
  const [mapSearchBusy, setMapSearchBusy] = useState(false);
  const [filterQuery,   setFilterQuery]   = useState('');

  // ── Tier 3 state ───────────────────────────────────────────────────────────
  const [measureMode,   setMeasureMode]   = useState(false);
  const [measurePts,    setMeasurePts]    = useState([]);
  const [weather,       setWeather]       = useState(null);
  const [weatherUnit,   setWeatherUnit]   = useState('C');
  const [exporting,     setExporting]     = useState(false);
  const [embedOpen,     setEmbedOpen]     = useState(false);
  const [embedCopied,   setEmbedCopied]   = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const mapElRef        = useRef(null);
  const mapRef          = useRef(null);
  const clusterRef      = useRef(null);
  const tileLayRef      = useRef(null);
  const markersRef      = useRef({});
  const scrubberRef     = useRef(null);
  const activeIdxRef    = useRef(activeIdx);
  const prevIdxRef      = useRef(activeIdx);
  // Tier 1
  const tileErrCountRef = useRef(0);
  const tileErrWinRef   = useRef(0);
  const offlineDetRef   = useRef(false);
  const recoveryTimRef  = useRef(null);
  const toastTimerRef   = useRef(null);
  // Tier 2
  const routeLineRef    = useRef(null);
  const heatLayersRef   = useRef([]);
  // Tier 3
  const measureGroupRef  = useRef(null);
  const weatherCacheRef  = useRef(new Map());
  const weatherTimRef    = useRef(null);
  const isFirstBuildRef  = useRef(false); // flips to true after the initial fitBounds
  activeIdxRef.current  = activeIdx;

  // ── iOS Safari --vh fix ────────────────────────────────────────────────────
  useEffect(() => {
    function setVh() {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    }
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg) => {
    setToast(msg);
    setToastVisible(true);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 3000);
  }, []);

  // ── Tile error / offline fallback ──────────────────────────────────────────
  const attachTileHandlers = useCallback((tileLayer, isFallback = false) => {
    tileLayer.on('tileload', () => {
      if (offlineDetRef.current) {
        offlineDetRef.current = false;
        setOfflineBanner(false);
      }
    });
    tileLayer.on('tileerror', () => {
      const now = Date.now();
      if (now - tileErrWinRef.current > 10000) {
        tileErrWinRef.current   = now;
        tileErrCountRef.current = 0;
      }
      tileErrCountRef.current += 1;
      if (tileErrCountRef.current < 5) return;

      if (isFallback) {
        if (!offlineDetRef.current) {
          offlineDetRef.current = true;
          setOfflineBanner(true);
          clearInterval(recoveryTimRef.current);
          recoveryTimRef.current = setInterval(() => {
            fetch('https://tile.openstreetmap.org/0/0/0.png', { cache: 'no-store', mode: 'no-cors' })
              .then(() => {
                clearInterval(recoveryTimRef.current);
                tileErrCountRef.current = 0;
                offlineDetRef.current   = false;
                setOfflineBanner(false);
                setTileStyle(detectDarkTheme() ? 'dark' : 'standard');
              })
              .catch(() => {});
          }, 30000);
        }
      } else {
        tileErrCountRef.current = 0;
        const map = mapRef.current;
        if (!map) return;
        if (tileLayRef.current) map.removeLayer(tileLayRef.current);
        const fb = TILES.fallback;
        const fallbackLayer = L.tileLayer(fb.url, { attribution: fb.attr, maxZoom: 18, subdomains: fb.sub }).addTo(map);
        attachTileHandlers(fallbackLayer, true);
        tileLayRef.current = fallbackLayer;
      }
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    clearInterval(recoveryTimRef.current);
    clearTimeout(toastTimerRef.current);
    clearTimeout(weatherTimRef.current);
  }, []);

  // Clamp activeIdx when events shrink
  useEffect(() => {
    if (sorted.length > 0 && activeIdx >= sorted.length) setActiveIdx(sorted.length - 1);
  }, [sorted.length]);

  // ── Initialize Leaflet map ─────────────────────────────────────────────────
  useEffect(() => {
    const el = mapElRef.current;
    if (!el || mapRef.current) return;

    const validCenter = typeof mapCenter?.lat === 'number' && isFinite(mapCenter.lat) &&
                        typeof mapCenter?.lng === 'number' && isFinite(mapCenter.lng);
    const initCenter = validCenter ? [mapCenter.lat, mapCenter.lng] : [20, 0];
    const initZoom   = validCenter ? (mapCenter.zoom || 6) : 2;
    const map = L.map(el, { center: initCenter, zoom: initZoom, zoomControl: false });
    L.control.zoom({ position: 'topright' }).addTo(map);

    const initStyle = detectDarkTheme() ? 'dark' : 'standard';
    const t = TILES[initStyle];
    const tileLayer = L.tileLayer(t.url, { attribution: t.attr, maxZoom: 18, subdomains: t.sub }).addTo(map);
    attachTileHandlers(tileLayer, false);
    tileLayRef.current = tileLayer;

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 60,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (c) => makeClusterIcon(c.getChildCount(), primary),
    });
    clusterRef.current = cluster;
    map.addLayer(cluster);

    mapRef.current = map;
    requestAnimationFrame(() => map.invalidateSize());
    setMapReady(true);

    return () => {
      clearInterval(recoveryTimRef.current);
      map.remove();
      mapRef.current     = null;
      clusterRef.current = null;
      tileLayRef.current = null;
      markersRef.current = {};
    };
  }, []);

  // ── Build markers ──────────────────────────────────────────────────────────
  useEffect(() => {
    const map     = mapRef.current;
    const cluster = clusterRef.current;
    if (!map || !cluster || !mapReady) return;

    cluster.clearLayers();
    markersRef.current = {};
    const curIdx = activeIdxRef.current;

    sorted.forEach((ev, idx) => {
      if (!hasValidCoords(ev)) return;
      const isActive = idx === curIdx;
      const catColor = categoryColor(ev, primary);
      let marker;
      try {
        marker = L.marker([ev.location.lat, ev.location.lng], {
          icon: makePinIcon(isActive, primary, secondary, catColor),
        });
      } catch (err) {
        console.warn('[MapTimeline] Marker creation failed:', ev.id, err);
        return;
      }

      marker.bindTooltip(ev.title, { permanent: false, direction: 'top', className: 'mp-tooltip' });

      const photo   = ev.photo || ev.photos?.[0];
      const dateStr = formatDate(ev.startDate);
      marker.bindPopup(
        `<div class="mp-popup">
          ${photo ? `<div class="mp-popup-img-wrap"><img src="${photo}" alt="" class="mp-popup-img" loading="lazy"/></div>` : ''}
          <div class="mp-popup-body">
            <div class="mp-popup-title">${ev.title}</div>
            <div class="mp-popup-date">${dateStr}</div>
            ${ev.category ? `<div class="mp-popup-cat" style="background:${catColor}22;color:${catColor};border:1px solid ${catColor}44;">${ev.category}</div>` : ''}
            <button class="mp-popup-view" data-ev-idx="${idx}">View Details</button>
          </div>
        </div>`,
        { className: 'mp-popup-pane', maxWidth: 260, minWidth: 180 }
      );

      marker.on('popupopen', () => {
        const btn = marker.getPopup()?.getElement()?.querySelector('.mp-popup-view');
        if (btn) btn.onclick = () => { onEventClick?.(ev); marker.closePopup(); };
      });
      marker.on('click', () => setActiveIdx(idx));

      cluster.addLayer(marker);
      markersRef.current[ev.id] = marker;
    });

    // On first load: fit all located events in view instead of jumping to one pin.
    // On subsequent rebuilds (event edits): skip so the user's pan/zoom is preserved.
    const pts = sorted.filter(hasValidCoords).map(e => [e.location.lat, e.location.lng]);
    if (pts.length > 0) {
      if (!isFirstBuildRef.current) {
        isFirstBuildRef.current = true;
        try {
          if (pts.length === 1) {
            map.setView(pts[0], 8, { animate: false });
          } else {
            map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], animate: false });
          }
        } catch (err) {
          console.warn('[MapTimeline] fitBounds/setView failed:', err);
        }
      }
    }
  }, [mapReady, sorted, primary, secondary]);

  // ── Update pin icons on activeIdx change ───────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const markers = markersRef.current;
    const prev    = sorted[prevIdxRef.current];
    const current = sorted[activeIdx];
    if (prev    && markers[prev.id])    markers[prev.id].setIcon(makePinIcon(false, primary, secondary, categoryColor(prev, primary)));
    if (current && markers[current.id]) markers[current.id].setIcon(makePinIcon(true, primary, secondary, categoryColor(current, primary)));
    prevIdxRef.current = activeIdx;
  }, [activeIdx, mapReady, sorted, primary, secondary]);

  // ── Fly to active event ────────────────────────────────────────────────────
  // Always fly — the previous-index check was unreliable because the pin-icon
  // effect (same dependency) runs first and clobbers prevIdxRef before we read it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const ev = sorted[activeIdx];
    if (!hasValidCoords(ev)) return;
    try {
      map.flyTo([ev.location.lat, ev.location.lng], ev.location.zoom || 5, { duration: 1.2, easeLinearity: 0.5 });
    } catch (err) {
      console.warn('[MapTimeline] flyTo failed:', err);
    }
  }, [activeIdx, mapReady, sorted]);

  // ── Switch tile layers ─────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (tileLayRef.current) map.removeLayer(tileLayRef.current);
    const t = TILES[tileStyle];
    const newLayer = L.tileLayer(t.url, { attribution: t.attr, maxZoom: 18, subdomains: t.sub }).addTo(map);
    attachTileHandlers(newLayer, false);
    tileLayRef.current = newLayer;
    tileErrCountRef.current = 0;
    tileErrWinRef.current   = 0;
  }, [tileStyle, mapReady]);

  // ── Auto-sync tile with app theme ──────────────────────────────────────────
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTileStyle(prev => (prev === 'terrain' || prev === 'satellite') ? prev : detectDarkTheme() ? 'dark' : 'standard');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => observer.disconnect();
  }, []);

  // ── Route polyline ─────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (routeLineRef.current) { map.removeLayer(routeLineRef.current); routeLineRef.current = null; }
    if (!showRoute) return;
    const pts = sorted.filter(hasValidCoords).map(e => [e.location.lat, e.location.lng]);
    if (pts.length < 2) return;
    routeLineRef.current = L.polyline(pts, { color: primary, weight: 2.5, opacity: 0.65, dashArray: '7 5' }).addTo(map);
  }, [showRoute, mapReady, sorted, primary]);

  // ── Heatmap circles ────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    heatLayersRef.current.forEach(c => map.removeLayer(c));
    heatLayersRef.current = [];
    if (!showHeatmap) return;
    sorted.filter(hasValidCoords).forEach(ev => {
      const col = categoryColor(ev, primary);
      const c   = L.circleMarker([ev.location.lat, ev.location.lng], {
        radius: 30, fillColor: col, fillOpacity: 0.18, stroke: false, interactive: false, pane: 'shadowPane',
      }).addTo(map);
      heatLayersRef.current.push(c);
    });
  }, [showHeatmap, mapReady, sorted, primary]);

  // ── Measurement tool ───────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!measureMode) return;

    map.doubleClickZoom.disable();
    map.getContainer().style.cursor = 'crosshair';

    const onMapClick = (e) => {
      setMeasurePts(pts => [...pts, { lat: e.latlng.lat, lng: e.latlng.lng }]);
    };
    map.on('click', onMapClick);

    return () => {
      map.off('click', onMapClick);
      map.doubleClickZoom.enable();
      map.getContainer().style.cursor = '';
    };
  }, [measureMode, mapReady]);

  // Redraw measurement overlay when points change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (measureGroupRef.current) { map.removeLayer(measureGroupRef.current); measureGroupRef.current = null; }
    if (measurePts.length === 0) return;

    const group = L.layerGroup();

    // Circle at each point
    measurePts.forEach(pt => {
      L.circleMarker([pt.lat, pt.lng], {
        radius: 5, fillColor: '#fff', fillOpacity: 1, color: primary, weight: 2, interactive: false,
      }).addTo(group);
    });

    if (measurePts.length >= 2) {
      L.polyline(measurePts.map(p => [p.lat, p.lng]), {
        color: '#fff', weight: 2, dashArray: '5 3', interactive: false,
      }).addTo(group);

      let cumulative = 0;
      for (let i = 1; i < measurePts.length; i++) {
        const a = measurePts[i - 1];
        const b = measurePts[i];
        const d = kmBetween(a.lat, a.lng, b.lat, b.lng);
        cumulative += d;
        L.marker([(a.lat + b.lat) / 2, (a.lng + b.lng) / 2], {
          icon: L.divIcon({ className: '', html: `<div class="mp-measure-tag">${formatKm(d)}</div>`, iconSize: null, iconAnchor: [0, 0] }),
          interactive: false, zIndexOffset: 500,
        }).addTo(group);
      }

      if (measurePts.length > 2) {
        const last = measurePts[measurePts.length - 1];
        L.marker([last.lat, last.lng], {
          icon: L.divIcon({ className: '', html: `<div class="mp-measure-total">Total: ${formatKm(cumulative)}</div>`, iconSize: null, iconAnchor: [0, 14] }),
          interactive: false, zIndexOffset: 600,
        }).addTo(group);
      }
    }

    group.addTo(map);
    measureGroupRef.current = group;
  }, [measurePts, mapReady, primary]);

  // ESC key: exit measure mode
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  setActiveIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setActiveIdx(i => Math.min(sorted.length - 1, i + 1));
      if (e.key === 'Escape' && measureMode) {
        setMeasureMode(false);
        setMeasurePts([]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sorted.length, measureMode]);

  // ── Weather fetch (Open-Meteo, free, no API key) ────────────────────────────
  useEffect(() => {
    const ev = sorted[activeIdx];
    if (!hasValidCoords(ev)) { setWeather(null); return; }

    const lat = ev.location.lat;
    const lng = ev.location.lng;
    const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;

    if (weatherCacheRef.current.has(key)) {
      setWeather(weatherCacheRef.current.get(key));
      return;
    }

    setWeather({ loading: true });
    clearTimeout(weatherTimRef.current);
    weatherTimRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code&forecast_days=1&timezone=auto`,
          { cache: 'default' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const temp = Math.round(data.current.temperature_2m);
        const code = data.current.weather_code;
        const [desc, icon] = WMO[code] ?? ['Unknown', '🌡️'];
        const result = { temp, desc, icon, loading: false, error: null };
        weatherCacheRef.current.set(key, result);
        setWeather(result);
      } catch {
        setWeather({ loading: false, error: true });
      }
    }, 600);

    return () => clearTimeout(weatherTimRef.current);
  }, [activeIdx, sorted]);

  // ── Scrubber scroll sync ───────────────────────────────────────────────────
  useEffect(() => {
    const track = scrubberRef.current;
    if (!track) return;
    const card = track.querySelector(`[data-ev-id="${sorted[activeIdx]?.id}"]`);
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeIdx, sorted]);

  // ── Auto-play ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!playing) { setProgress(0); return; }
    const TICK = 50, TOTAL = SPEEDS[playSpeed].ms;
    let elapsed = 0;
    const id = setInterval(() => {
      elapsed += TICK;
      setProgress((elapsed / TOTAL) * 100);
      if (elapsed >= TOTAL) {
        elapsed = 0;
        setActiveIdx(i => { if (i + 1 >= sorted.length) { setPlaying(false); return 0; } return i + 1; });
      }
    }, TICK);
    return () => clearInterval(id);
  }, [playing, playSpeed, sorted.length]);

  // ── Callbacks ──────────────────────────────────────────────────────────────

  const fitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const pts = sorted.filter(hasValidCoords).map(e => [e.location.lat, e.location.lng]);
    if (pts.length === 0) return;
    if (pts.length === 1) { map.flyTo(pts[0], 8); return; }
    map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], animate: true });
  }, [sorted]);

  const handleMapSearch = useCallback(async (e) => {
    e.preventDefault();
    const q = mapSearch.trim();
    if (!q) return;
    setMapSearchBusy(true);
    try {
      const results = await nominatimSearch(q);
      if (results?.[0]) {
        const lat = parseFloat(results[0].lat);
        const lng = parseFloat(results[0].lon);
        if (isFinite(lat) && isFinite(lng)) {
          mapRef.current?.flyTo([lat, lng], 10, { duration: 1.2 });
        }
        setMapSearch('');
      } else {
        showToast('Location not found');
      }
    } catch (err) {
      showToast(err.status === 429 ? err.message : 'Search failed — check your connection');
    } finally {
      setMapSearchBusy(false);
    }
  }, [mapSearch, showToast]);

  const exportMap = useCallback(async (format) => {
    const mapEl = mapElRef.current;
    if (!mapEl || exporting) return;
    setExporting(true);
    showToast(`Preparing ${format.toUpperCase()}…`);
    try {
      const canvas  = await getMapCanvas(mapEl);
      const dataUrl = canvas.toDataURL('image/png');
      if (format === 'png') {
        const a = document.createElement('a');
        a.href     = dataUrl;
        a.download = 'map-timeline.png';
        a.click();
      } else {
        const { width, height } = canvas;
        const pdf = new jsPDF({
          orientation: width > height ? 'landscape' : 'portrait',
          unit:   'px',
          format: [width, height],
        });
        pdf.addImage(dataUrl, 'PNG', 0, 0, width, height);
        pdf.save('map-timeline.pdf');
      }
      showToast(`${format.toUpperCase()} saved`);
    } catch (err) {
      console.error('[MapTimeline] Export failed:', err);
      showToast('Export failed — try again');
    } finally {
      setExporting(false);
    }
  }, [exporting, showToast]);

  const navigate = (dir) => setActiveIdx(i => Math.max(0, Math.min(sorted.length - 1, i + dir)));

  // ── Derived ────────────────────────────────────────────────────────────────

  const eventsWithLoc  = sorted.filter(hasValidCoords);
  const activeEv       = sorted[activeIdx];
  const activeDesc     = activeEv ? stripHtml(activeEv.description || '').slice(0, 180) : '';
  const activePhoto    = activeEv ? (activeEv.photo || activeEv.photos?.[0]) : null;

  const filterLower    = filterQuery.toLowerCase();
  const filteredEvents = filterQuery
    ? sorted.filter(ev => ev.title?.toLowerCase().includes(filterLower) || ev.description?.toLowerCase().includes(filterLower))
    : sorted;

  const routeDist = showRoute && eventsWithLoc.length >= 2 ? routeDistance(sorted) : 0;

  const measureDist = useMemo(() => {
    if (measurePts.length < 2) return 0;
    let d = 0;
    for (let i = 1; i < measurePts.length; i++) {
      d += kmBetween(measurePts[i-1].lat, measurePts[i-1].lng, measurePts[i].lat, measurePts[i].lng);
    }
    return d;
  }, [measurePts]);

  const displayTemp = weather && !weather.loading && !weather.error
    ? (weatherUnit === 'C' ? `${weather.temp}°C` : `${Math.round(weather.temp * 9/5 + 32)}°F`)
    : null;

  if (sorted.length === 0) {
    return (
      <div className="mp-empty-root">
        <div className="mp-empty-msg">No events yet. Add events to see them on the map.</div>
      </div>
    );
  }

  const embedUrl  = timeline ? `${window.location.origin}/embed/${timeline.id}` : '';
  const safeEmbedTitle = (timeline?.title || 'Timeline').replace(/"/g, '&quot;');
  const embedCode = `<iframe\n  src="${embedUrl}"\n  width="100%"\n  height="500"\n  style="border:none;border-radius:12px;"\n  title="${safeEmbedTitle}"\n  allowfullscreen\n></iframe>`;

  return (
    <>
    <div className="mp-root" style={{ '--mp-primary': primary, '--mp-secondary': secondary }}>

      {/* ── Map zone ────────────────────────────────────────────────────────── */}
      <div className="mp-map-zone">
        <div ref={mapElRef} className="mp-map-el" />

        {/* Offline banner */}
        {offlineBanner && (
          <div className="mp-offline-banner" role="alert">
            <span>Map tiles unavailable — check your connection. Retrying automatically.</span>
            <button className="mp-offline-dismiss" onClick={() => setOfflineBanner(false)} aria-label="Dismiss">✕</button>
          </div>
        )}

        {/* Measure mode banner */}
        {measureMode && (
          <div className="mp-measure-banner" role="status">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12h4M18 12h4M12 2v4M12 18v4" />
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
            {measurePts.length === 0
              ? 'Click on the map to start measuring'
              : measurePts.length === 1
              ? 'Click again to add more points'
              : `${measurePts.length} points · ${formatKm(measureDist)}`
            }
            {measurePts.length > 0 && (
              <button className="mp-measure-clear" onClick={() => setMeasurePts([])} title="Clear points">Clear</button>
            )}
            <button className="mp-measure-done" onClick={() => { setMeasureMode(false); setMeasurePts([]); }}>Done</button>
          </div>
        )}

        {/* Toast */}
        <div className={`mp-toast${toastVisible ? ' mp-toast-visible' : ''}`} role="status" aria-live="polite">
          {toast}
        </div>

        {/* Event preview panel */}
        {activeEv && showPreview && (
          <div className="mp-preview">
            <button className="mp-preview-close" onClick={() => setShowPreview(false)} aria-label="Close preview">✕</button>
            {activePhoto && <img src={activePhoto} alt="" className="mp-preview-img" />}
            {(() => {
              const src = toYtEmbedUrl(activeEv.videoUrl);
              if (!src) return null;
              return (
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', overflow: 'hidden' }}>
                  <iframe
                    src={src}
                    title={activeEv.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                  />
                </div>
              );
            })()}
            <div className="mp-preview-body">
              <div className="mp-preview-date">{formatDate(activeEv.startDate)}</div>
              <div className="mp-preview-title">{activeEv.title}</div>

              {activeEv.category && (
                <div className="mp-preview-cat" style={{
                  background: `${categoryColor(activeEv, primary)}22`,
                  color:       categoryColor(activeEv, primary),
                  border:     `1px solid ${categoryColor(activeEv, primary)}44`,
                }}>
                  {activeEv.category}
                </div>
              )}

              {activeEv.location?.name && (
                <div className="mp-preview-loc">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
                  </svg>
                  {activeEv.location.name}
                </div>
              )}

              {/* Weather */}
              {weather && !weather.error && hasValidCoords(activeEv) && (
                <div className="mp-weather">
                  {weather.loading ? (
                    <span className="mp-weather-loading">Loading weather…</span>
                  ) : (
                    <>
                      <span className="mp-weather-icon">{weather.icon}</span>
                      <span className="mp-weather-desc">{weather.desc}</span>
                      <button
                        className="mp-weather-temp"
                        onClick={() => setWeatherUnit(u => u === 'C' ? 'F' : 'C')}
                        title="Toggle °C / °F"
                      >
                        {displayTemp}
                      </button>
                    </>
                  )}
                </div>
              )}

              {activeDesc && (
                <p className="mp-preview-desc">{activeDesc}{activeDesc.length === 180 ? '…' : ''}</p>
              )}
              <button className="mp-preview-view" onClick={() => onEventClick?.(activeEv)} style={{ background: primary }}>
                View Details
              </button>
            </div>
          </div>
        )}

        {activeEv && !showPreview && (
          <button className="mp-preview-reopen" onClick={() => setShowPreview(true)} title="Show event preview" style={{ borderColor: primary, color: primary }}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
            </svg>
            Info
          </button>
        )}

        {/* Map controls cluster — bottom-left */}
        <div className="mp-map-controls">
          {eventsWithLoc.length > 0 && (
            <button className="mp-ctrl-btn" onClick={fitAll} title="Zoom to show all pins">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
              Fit All
            </button>
          )}

          {eventsWithLoc.length >= 2 && (
            <button
              className={`mp-ctrl-btn${showRoute ? ' mp-ctrl-active' : ''}`}
              onClick={() => setShowRoute(r => !r)}
              style={showRoute ? { borderColor: primary, color: primary } : {}}
              title="Toggle route line"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <path d="M3 17c3-3 6-3 9 0s6 3 9 0" />
                <circle cx="3" cy="17" r="1.5" fill="currentColor" stroke="none" />
                <circle cx="21" cy="17" r="1.5" fill="currentColor" stroke="none" />
              </svg>
              Route
              {showRoute && routeDist > 0 && <span className="mp-ctrl-badge">{formatKm(routeDist)}</span>}
            </button>
          )}

          {eventsWithLoc.length > 0 && (
            <button
              className={`mp-ctrl-btn${showHeatmap ? ' mp-ctrl-active' : ''}`}
              onClick={() => setShowHeatmap(h => !h)}
              style={showHeatmap ? { borderColor: primary, color: primary } : {}}
              title="Toggle heatmap"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                <circle cx="12" cy="12" r="4" opacity="0.9" />
                <circle cx="12" cy="12" r="8" opacity="0.35" />
                <circle cx="12" cy="12" r="11" opacity="0.15" />
              </svg>
              Heat
            </button>
          )}

          <button
            className={`mp-ctrl-btn${measureMode ? ' mp-ctrl-active' : ''}`}
            onClick={() => {
              if (measureMode) { setMeasureMode(false); setMeasurePts([]); }
              else setMeasureMode(true);
            }}
            style={measureMode ? { borderColor: primary, color: primary } : {}}
            title="Measure distances on the map (Esc to cancel)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <path d="M2 12h20M5 8l-3 4 3 4M19 8l3 4-3 4" />
            </svg>
            Measure
          </button>

          {/* Export */}
          <button
            className="mp-ctrl-btn"
            onClick={() => exportMap('png')}
            disabled={exporting}
            title="Export map as PNG image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            {exporting ? 'Exporting…' : 'PNG'}
          </button>

          <button
            className="mp-ctrl-btn"
            onClick={() => exportMap('pdf')}
            disabled={exporting}
            title="Export map as PDF"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="9" y1="13" x2="15" y2="13" />
              <line x1="9" y1="17" x2="15" y2="17" />
            </svg>
            PDF
          </button>

          {!isEmbed && timeline && (
            <button
              className="mp-ctrl-btn"
              onClick={() => timeline.privacy === 'public' && setEmbedOpen(true)}
              disabled={timeline.privacy !== 'public'}
              title={timeline.privacy === 'public' ? 'Get embed code' : 'Make this timeline public to enable embedding'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
              </svg>
              Embed
            </button>
          )}
        </div>

        {/* Map location search */}
        <form className="mp-map-search" onSubmit={handleMapSearch}>
          <input
            className="mp-map-search-input"
            type="text"
            placeholder="Jump to location…"
            value={mapSearch}
            onChange={e => setMapSearch(e.target.value)}
            aria-label="Search for a map location"
          />
          <button
            type="submit"
            className="mp-map-search-btn"
            disabled={mapSearchBusy || !mapSearch.trim()}
            aria-label="Search"
          >
            {mapSearchBusy ? <span className="mp-search-spinner" /> : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="22" y2="22" />
              </svg>
            )}
          </button>
        </form>

        {/* Tile toggle */}
        <div className="mp-tile-toggle">
          {[
            { id: 'standard',  label: 'Standard'     },
            { id: 'terrain',   label: 'Terrain View'  },
            { id: 'satellite', label: 'Satellite'     },
            { id: 'dark',      label: 'Dark'          },
          ].map(({ id, label }) => (
            <button
              key={id}
              className={`mp-tile-btn${tileStyle === id ? ' mp-tile-active' : ''}`}
              onClick={() => setTileStyle(id)}
              title={`${label} map tiles`}
            >
              {label}
            </button>
          ))}
        </div>

        {eventsWithLoc.length === 0 && (
          <div className="mp-no-pins-overlay">
            No events have location data yet. Edit events to add locations.
          </div>
        )}
      </div>

      {/* ── Scrubber zone ───────────────────────────────────────────────────── */}
      <div className="mp-scrubber-zone">
        {/* Event filter */}
        <div className="mp-filter-row">
          <div className="mp-filter-wrap">
            <svg className="mp-filter-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
              <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="22" y2="22" />
            </svg>
            <input
              className="mp-filter-input"
              type="text"
              placeholder="Filter events…"
              value={filterQuery}
              onChange={e => setFilterQuery(e.target.value)}
              aria-label="Filter events by title or description"
            />
            {filterQuery && (
              <button className="mp-filter-clear" onClick={() => setFilterQuery('')} aria-label="Clear filter">✕</button>
            )}
          </div>
          {filterQuery && <span className="mp-filter-count">{filteredEvents.length} / {sorted.length}</span>}
        </div>

        {/* Card track */}
        <div className="mp-scrubber-row">
          <button className="mp-nav-btn" onClick={() => navigate(-1)} disabled={activeIdx === 0} aria-label="Previous event">‹</button>

          <div ref={scrubberRef} className="mp-scrubber-track" role="listbox" aria-label="Timeline events">
            {filteredEvents.map((ev) => {
              const hasLoc   = hasValidCoords(ev);
              const realIdx  = sorted.indexOf(ev);
              const isActive = realIdx === activeIdx;
              const catCol   = categoryColor(ev, primary);
              return (
                <button
                  key={ev.id}
                  data-ev-id={ev.id}
                  className={`mp-card${isActive ? ' mp-card-active' : ''}${!hasLoc ? ' mp-card-noloc' : ''}`}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { setActiveIdx(realIdx); if (!hasLoc) showToast('This event has no location set'); }}
                  title={ev.title}
                  style={isActive ? { borderColor: primary } : {}}
                >
                  {ev.category && <div className="mp-card-cat-dot" style={{ background: catCol }} title={ev.category} />}
                  <div className="mp-card-title">{ev.title}</div>
                  <div className="mp-card-date">{formatDate(ev.startDate)}</div>
                  {!hasLoc && <div className="mp-card-noloc-badge">No location</div>}
                </button>
              );
            })}
            {filteredEvents.length === 0 && (
              <div className="mp-filter-empty">No events match "{filterQuery}"</div>
            )}
          </div>

          <button className="mp-nav-btn" onClick={() => navigate(1)} disabled={activeIdx === sorted.length - 1} aria-label="Next event">›</button>
        </div>

        {/* Play controls */}
        <div className="mp-play-row">
          <button
            className={`mp-play-btn${playing ? ' mp-playing' : ''}`}
            onClick={() => setPlaying(p => !p)}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? (
              <><svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg> Pause</>
            ) : (
              <><svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><polygon points="5 3 19 12 5 21 5 3"/></svg> Play</>
            )}
          </button>

          <div className="mp-speed-group" aria-label="Playback speed">
            {SPEEDS.map((s, i) => (
              <button
                key={s.label}
                className={`mp-speed-btn${playSpeed === i ? ' mp-speed-active' : ''}`}
                onClick={() => setPlaySpeed(i)}
                title={`${s.label} — ${s.ms / 1000}s per event`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="mp-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin="0" aria-valuemax="100" style={{ opacity: playing ? 1 : 0.25 }}>
            <div className="mp-progress-fill" style={{ width: `${progress}%`, background: primary }} />
          </div>

          <span className="mp-counter" aria-live="polite">{activeIdx + 1} / {sorted.length}</span>
        </div>
      </div>
    </div>

    {/* ── Embed modal ── */}
    {embedOpen && timeline && (
      <Modal open onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }} title="Embed Timeline" width={540}>
        <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
          Paste this snippet into any webpage to embed <strong style={{ color: 'var(--text)' }}>{timeline.title}</strong> as an interactive iframe.
        </p>
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12 }}>
          <pre style={{ padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75, color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {embedCode}
          </pre>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
          Embed URL: <a href={embedUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{embedUrl}</a>
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}>Close</button>
          <button className="btn-primary" onClick={() => { navigator.clipboard?.writeText(embedCode).catch(() => {}); setEmbedCopied(true); setTimeout(() => setEmbedCopied(false), 2000); }} style={{ minWidth: 148 }}>
            {embedCopied ? 'Copied!' : 'Copy Snippet'}
          </button>
        </div>
      </Modal>
    )}
    </>
  );
};

export default MapTimeline;
